import { useCallback, useEffect, useState } from "react";
import { NewTableModal } from "./NewTableModal";
import { NewTableWaiterModal } from "./NewTableWaiterModal";
import { useNewTableWaiterChoice } from "./useNewTableWaiterChoice";
import type { NewTableWaiter } from "./newTableWaiterOrder";

interface NewTableFlowProps {
  open: boolean;
  onClose: () => void;
  /** `waiterId` — само когато в първия модал е избран ДРУГ сервитьор. */
  onSubmit: (tableName: string, waiterId?: number) => void;
  /** „Предлагане на номер за нова маса" — подава се на `NewTableModal`. */
  suggestName?: boolean;
}

/**
 * „Нова маса" от списъка. С правото „Откриване и закриване на сметки на други
 * оператори" (`useNewTableWaiterChoice`) са два модала един след друг: първо
 * на кой сервитьор е масата (`NewTableWaiterModal`, по подразбиране влезлият;
 * докосване маркира, „Избери" продължава), после номерът (`NewTableModal`,
 * чийто „Назад" връща към сервитьора). Без правото — и в локален режим, където
 * касата не отваря маса на друг сервитьор — само номерът, както досега.
 */
export const NewTableFlow = ({ open, onClose, onSubmit, suggestName }: NewTableFlowProps) => {
  const waiterChoice = useNewTableWaiterChoice();
  const [waiterChosen, setWaiterChosen] = useState(false);
  // Избраният в първия модал; null — влезлият.
  const [chosenWaiterId, setChosenWaiterId] = useState<number | null>(null);

  const canChooseWaiter = !!waiterChoice && !waiterChoice.lockedReason;
  const waiterModalOpen = open && canChooseWaiter && !waiterChosen;

  // Локален режим при отворен прозорец: към номера, масата е на влезлия. Като
  // „избран" остава и след връщането на връзката — номерът, който се пише,
  // не скача обратно към сервитьора.
  useEffect(() => {
    if (!open || !waiterChoice || canChooseWaiter) return;
    setWaiterChosen(true);
    setChosenWaiterId(null);
  }, [open, waiterChoice, canChooseWaiter]);

  const close = () => {
    setWaiterChosen(false);
    setChosenWaiterId(null);
    onClose();
  };

  const selectWaiter = useCallback((waiter: NewTableWaiter) => {
    setChosenWaiterId(waiter.isMe ? null : waiter.id);
  }, []);
  const confirmWaiter = useCallback(() => setWaiterChosen(true), []);

  return (
    <>
      {waiterChoice && (
        <NewTableWaiterModal
          open={waiterModalOpen}
          me={waiterChoice.me}
          selectedWaiterId={chosenWaiterId ?? waiterChoice.me.id}
          onSelect={selectWaiter}
          onConfirm={confirmWaiter}
          onClose={close}
        />
      )}
      <NewTableModal
        open={open && !waiterModalOpen}
        onClose={close}
        onSubmit={onSubmit}
        suggestName={suggestName}
        // Минал ли е в локален режим междувременно — масата е на влезлия.
        waiterId={canChooseWaiter ? (chosenWaiterId ?? undefined) : undefined}
        onBack={canChooseWaiter ? () => setWaiterChosen(false) : undefined}
      />
    </>
  );
};
